import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SesionApiService } from '@core/services/api/sesion.service';
import { EmpleadoApiService } from '@core/services/api/empleado-api.service';
import { AppService } from '@shared/services/app.service';
import { ToastService } from '@shared/services/toast.service';
import { Tabla } from '@shared/components/tabla/tabla';
import { FiltrosReporte } from '@shared/components/filtros-reporte/filtros-reporte';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { IEmpleadoData } from '@core/interfaces/entidades/empleado.entidad';
import { FormField, form } from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { SucursalService } from '@shared/services/sucursal.service';
import type { ISesion } from '@core/services/api/sesion.service';
import { UiButton, UiDialog, UiSelect, UiDatepicker, UiTooltip } from '@shared/components/ui';

interface SesionRow {
  id: number;
  usuario: string;
  email: string;
  usuario_id: number;
  dispositivo: string;
  navegador: string;
  so: string;
  estado: string;
  estado_activo: boolean;
  ultima_actividad: string;
  inicio: string;
}

@Component({
  selector: 'app-admin-sesiones',
  imports: [
    Tabla,
    FormsModule,
    UiButton,
    UiDialog,
    UiSelect,
    UiDatepicker,
    UiTooltip,
    FormField,
    FiltrosReporte,
  ],
  templateUrl: './admin-sesiones.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-sesiones.css',
})
export class AdminSesiones implements OnInit {
  constructor() {
    effect(
      () => {
        if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
          this.cargarEmpleados();
          this.cargarSesiones();
        }
      },
      { allowSignalWrites: true },
    );
  }

  private readonly sesionApi = inject(SesionApiService);
  private readonly empleadoApi = inject(EmpleadoApiService);
  private readonly appService = inject(AppService);
  private readonly toast = inject(ToastService);
  private readonly sucursalService = inject(SucursalService);
  private readonly datePipe = new DatePipe('es-PE');

  sesiones = signal<SesionRow[]>([]);
  total = signal(0);
  loading = signal(true);
  page = signal(1);
  limit = signal(20);
  columnas: ITablaColumna[] = [];

  opcionesEmpleado = signal<IOptionsSelect<IEmpleadoData | null>[]>([]);

  estadoOptions: IOptionsSelect<string | null>[] = [
    { label: 'Todas', value: null },
    { label: 'Activas', value: 'activa' },
    { label: 'Revocadas', value: 'revocada' },
  ];

  filtroForm = form(
    signal({
      empleado: null as IEmpleadoData | null,
      estado: null as string | null,
      fecha_desde: null as Date | null,
      fecha_hasta: null as Date | null,
      dispositivo: '',
    }),
  );

  modalRevocar = signal(false);
  sesionSeleccionada = signal<SesionRow | null>(null);

  modalRevocarTodas = signal(false);
  usuarioRevocarTodas = signal<SesionRow | null>(null);

  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Gestion de Sesiones',
      subtitle: 'Administra las sesiones activas de los usuarios de tu empresa.',
      showBackButton: true,
      icon: 'bi bi-shield-shaded',
    });
    this.columnas = [
      { key: 'usuario', titulo: 'Usuario' },
      { key: 'email', titulo: 'Email' },
      { key: 'dispositivo', titulo: 'Dispositivo' },
      { key: 'navegador', titulo: 'Navegador' },
      { key: 'so', titulo: 'SO' },
      { key: 'estado', titulo: 'Estado' },
      { key: 'ultima_actividad', titulo: 'Ultima actividad' },
      { key: 'inicio', titulo: 'Inicio' },
    ];
  }

  cargarSesiones() {
    this.loading.set(true);
    const f = this.filtroForm().value();
    const params: Record<string, string | number> = {
      page: this.page(),
      limit: this.limit(),
    };
    if (f.estado) params['estado'] = f.estado;
    if (f.dispositivo) params['dispositivo'] = f.dispositivo;
    if (f.fecha_desde) params['fecha_desde'] = this.formatISO(f.fecha_desde);
    if (f.fecha_hasta) params['fecha_hasta'] = this.formatISO(f.fecha_hasta);
    if (f.empleado?.id) params['usuario_id'] = f.empleado.id;

    this.sesionApi.obtenerSesionesEmpresa(params).subscribe({
      next: (res) => {
        this.sesiones.set(this.mapRows(res.data ?? []));
        this.total.set(res.total ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudieron cargar las sesiones.',
        );
        this.loading.set(false);
      },
    });
  }

  aplicarFiltros(event?: Event) {
    if (event) event.preventDefault();
    this.page.set(1);
    this.cargarSesiones();
  }

  limpiarFiltros() {
    this.filtroForm().value.set({
      empleado: null,
      estado: null,
      fecha_desde: null,
      fecha_hasta: null,
      dispositivo: '',
    });
    this.aplicarFiltros();
  }

  revocarSesion(row: SesionRow) {
    this.sesionSeleccionada.set(row);
    this.modalRevocar.set(true);
  }

  confirmarRevocar() {
    const sesion = this.sesionSeleccionada();
    if (!sesion) return;
    this.sesionApi.revocarSesion(sesion.usuario_id, sesion.id).subscribe({
      next: () => {
        this.toast.show(
          'top-right',
          'success',
          'Sesion revocada',
          'La sesion fue revocada exitosamente.',
        );
        this.modalRevocar.set(false);
        this.cargarSesiones();
      },
      error: (err) =>
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo revocar la sesion.',
        ),
    });
  }

  confirmarRevocarTodasUsuario(row: SesionRow) {
    this.usuarioRevocarTodas.set(row);
    this.modalRevocarTodas.set(true);
  }

  confirmarRevocarTodas() {
    const row = this.usuarioRevocarTodas();
    if (!row) return;
    this.sesionApi.revocarTodasSesiones(row.usuario_id).subscribe({
      next: (res: any) => {
        this.toast.show(
          'top-right',
          'success',
          'Sesiones revocadas',
          `${res?.revocadas ?? ''} sesiones revocadas.`,
        );
        this.modalRevocarTodas.set(false);
        this.cargarSesiones();
      },
      error: (err) =>
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudieron revocar las sesiones.',
        ),
    });
  }

  cambiarPagina(delta: number) {
    const nueva = this.page() + delta;
    if (nueva < 1 || nueva > this.maxPage()) return;
    this.page.set(nueva);
    this.cargarSesiones();
  }

  maxPage = computed(() => Math.max(1, Math.ceil(this.total() / this.limit())));

  private cargarEmpleados() {
    this.empleadoApi.cargar().subscribe({
      next: (rs) => {
        this.opcionesEmpleado.set([
          { label: 'Todos los empleados', value: null },
          ...rs.map((e) => ({ label: e.nombre_completo, value: e })),
        ]);
      },
      error: (err) => {
        if (err.status === 401) return;
        this.toast.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudieron cargar los empleados.',
        );
      },
    });
  }

  private mapRows(data: ISesion[]): SesionRow[] {
    return data.map((s) => ({
      id: s.id,
      usuario: s.nombre_completo,
      email: s.email,
      usuario_id: s.usuario_id,
      dispositivo: s.device_name,
      navegador: s.browser,
      so: s.operating_system,
      estado: s.is_active ? 'Activa' : 'Revocada',
      estado_activo: s.is_active,
      ultima_actividad: this.formatDate(s.last_activity),
      inicio: this.formatDate(s.created_at),
    }));
  }

  private formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    return this.datePipe.transform(dateStr, 'dd/MM/yy HH:mm') ?? '-';
  }

  private formatISO(date: Date): string {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString().split('T')[0];
  }
}
